<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import LandingCtaButton from './LandingCtaButton.vue';

const { t } = useI18n();
const { animateInView, slideUp } = useScrollAnimations();
const sectionRef = ref(null);
const cardsRef = ref([]);

const steps = computed(() => [
    {
        step: t('landingPage.v2.steps.items[0].step'),
        duration: t('landingPage.v2.steps.items[0].duration'),
        icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-16 h-16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>`,
        title: t('landingPage.v2.steps.items[0].title'),
    },
    {
        step: t('landingPage.v2.steps.items[1].step'),
        duration: t('landingPage.v2.steps.items[1].duration'),
        icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-16 h-16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
            </svg>`,
        title: t('landingPage.v2.steps.items[1].title'),
    },
    {
        step: t('landingPage.v2.steps.items[2].step'),
        duration: t('landingPage.v2.steps.items[2].duration'),
        icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-16 h-16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            </svg>`,
        title: t('landingPage.v2.steps.items[2].title'),
    }
]);

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
            cardsRef.value.forEach((card, index) => {
                slideUp(card, index * 0.4);
            });
        });
    }
});
</script>

<template>
    <div class="py-24 relative" ref="sectionRef">
        <!-- Background elements if needed -->
        <div class="mx-auto max-w-7xl px-6 lg:px-8">

            <!-- Header -->
            <div class="max-w-3xl mb-16">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl mb-6"
                    v-html="$t('landingPage.v2.steps.header.title')">
                </h2>
                <p class="text-lg text-gray-400 leading-relaxed" v-html="$t('landingPage.v2.steps.header.subtitle')">
                </p>
            </div>

            <!-- Steps Flex Container -->
            <div class="flex flex-col md:flex-row gap-4 items-center justify-center relative mb-16">

                <template v-for="(step, index) in steps" :key="index">
                    <!-- Step Card -->
                    <div ref="cardsRef"
                        class="bg-black-50 border border-white/50 rounded-xl p-6 flex flex-col items-center text-center relative z-10 opacity-0 aspect-[1/1] w-full md:w-auto md:flex-1 max-w-sm justify-center hover:border-terciary/50 transition-colors duration-300">
                        <div class="text-terciary font-bold text-2xl mb-2">{{ step.step }}</div>
                        <div class="text-gray-400 text-md mb-6">{{ step.duration }}</div>

                        <div class="mb-6 text-terciary" v-html="step.icon"></div>

                        <h3 class="text-white font-semibold text-2xl" v-html="step.title"></h3>
                    </div>

                    <!-- Arrow (only between steps) -->
                    <div v-if="index < steps.length - 1"
                        class="hidden md:block text-secondary transform -rotate-90 md:rotate-0 my-4 md:my-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3"
                            stroke="currentColor" class="w-8 h-8">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </div>
                    <!-- Mobile Arrow (vertical flow) -->
                    <div v-if="index < steps.length - 1" class="md:hidden text-secondary transform rotate-90 my-4">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="3"
                            stroke="currentColor" class="w-8 h-8">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                    </div>
                </template>

            </div>

            <!-- CTA -->
            <div class="flex justify-center">
                <LandingCtaButton sectionName="Steps" :text="$t('landingPage.v2.cta.steps')" />
            </div>

        </div>
    </div>
</template>

<style scoped></style>
